// app/components/privacy-graph-scrolly/sticky-controller.ts

import { useLayoutEffect, useSyncExternalStore, type RefObject } from "react";
import {
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import type { KeyframeBand } from "./types";

/** Where inside a beat's band the animations are considered "settled". */
const SETTLED_FRACTION = 0.7;

/**
 * Pure resolver: given raw scroll progress and the bands table, return the
 * "settled" progress value for the band the user is currently in. The settled
 * point sits at SETTLED_FRACTION (0.7) into the band, past every beat's main
 * animation window. Below the first band's start, returns the first band's
 * settled point.
 */
export function settledProgressFor(
  progress: number,
  bands: readonly KeyframeBand[],
): number {
  for (let i = bands.length - 1; i >= 0; i--) {
    // i is in [0, bands.length - 1]; bounds guaranteed by the loop.
    const b = bands[i]!;
    if (progress >= b.start) {
      return b.start + (b.end - b.start) * SETTLED_FRACTION;
    }
  }
  // bands is always non-empty (callers pass module-level constants).
  const b = bands[0]!;
  return b.start + (b.end - b.start) * SETTLED_FRACTION;
}

/**
 * Returns a spring-snapped scroll progress value for the sticky scrollytelling.
 *
 * IMPORTANT: outerRef MUST point to the OUTER scroll container — the tall
 * sizer that creates the scroll distance. Targeting the inner sticky element
 * makes progress jump to 1 immediately. (Spec §7 footgun.)
 *
 * The returned `progress` is NOT raw scrollY — it is a spring tracking the
 * "settled" point of whichever beat is currently active. This makes beat-to-beat
 * transitions feel like discrete snaps (~180ms) rather than scroll-coupled
 * tweening, while preserving each beat's internal animation choreography.
 */
export function useStickyController(
  outerRef: RefObject<HTMLElement | null>,
  bands: readonly KeyframeBand[],
) {
  const { scrollYProgress: rawProgress } = useScroll({
    target: outerRef,
    offset: ["start start", "end end"],
  });

  // Map raw progress → settled-progress target via the bands table.
  const snappedTarget = useTransform(rawProgress, (v) =>
    settledProgressFor(v, bands),
  );

  // Spring tracks the snapped target. Defaults-adjacent values (260/30) — feel-tune in Task 4 if needed.
  const scrollYProgress = useSpring(snappedTarget, {
    stiffness: 260,
    damping: 30,
  });

  // Avoid an initial spring "flourish" on deep-links / refreshes mid-section.
  // useScroll measures asynchronously; the spring would otherwise animate from
  // its first-frame value (settled-of-band-1) to the actual settled point.
  // Jump to the current target once after first measurement.
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      scrollYProgress.jump(snappedTarget.get());
    });
    return () => cancelAnimationFrame(id);
  }, [scrollYProgress, snappedTarget]);

  const hydrated = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  return {
    scrollYProgress: scrollYProgress as MotionValue<number>,
    hydrated,
  };
}
